import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { LandHealthQuiz } from '../components/interactive/LandHealthQuiz';
import { KarnatakaSeasonalWheel } from '../components/interactive/KarnatakaSeasonalWheel';
import { TiltCard } from '../components/effects/TiltCard';
import { RippleButton } from '../components/effects/RippleButton';
import { ParallaxHero } from '../components/effects/ParallaxHero';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1708593343700-a101f8fe4d11?w=1600&q=75&fm=webp";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const contactRef = useRef(null);
  
  // Contact form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    land_size: '',
    message: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects`);
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const categories = ['All', ...new Set(projects.map(p => p.category))];
  const filteredProjects = filter === 'All' 
    ? projects 
    : projects.filter(p => p.category === filter);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setFormSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        location: '',
        land_size: '',
        message: ''
      });
    } catch (err) {
      setFormError('Failed to submit. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <main className="min-h-screen" data-testid="projects-page">
      {/* Hero Section with Parallax */}
      <ParallaxHero
        imageSrc={HERO_IMAGE}
        imageAlt="Community forest restoration"
        className="h-[60vh] min-h-[500px] flex items-center justify-center"
        overlayClassName="bg-forest/70"
        parallaxIntensity={0.2}
      >
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-white px-6 max-w-4xl mx-auto">
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-sm tracking-[0.3em] uppercase mb-6 text-white/80"
            >
              Portfolio & Resources
            </motion.p>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-serif text-4xl sm:text-5xl md:text-6xl leading-tight mb-8"
              data-testid="projects-headline"
            >
              Our Projects
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-lg text-white/90 max-w-2xl mx-auto leading-relaxed"
            >
              Explore our ecological design work and discover tools to understand your own land.
            </motion.p>
          </div>
        </div>
      </ParallaxHero>

      {/* Land Health Quiz Section */}
      <section className="section-padding bg-cream" data-testid="quiz-section">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <p className="text-sm tracking-[0.2em] uppercase text-earth mb-4">Interactive Tool</p>
            <h2 className="font-serif text-3xl md:text-4xl text-forest mb-4">
              Land Health Quiz
            </h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Answer 5 questions about your land and get a personalized ecological health profile 
              with recommendations.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <LandHealthQuiz onComplete={scrollToContact} />
          </motion.div>
        </div>
      </section>

      {/* Karnataka Seasonal Wheel Section */}
      <section className="section-padding bg-sand" data-testid="seasonal-section">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <p className="text-sm tracking-[0.2em] uppercase text-earth mb-4">Hyperlocal Guide</p>
            <h2 className="font-serif text-3xl md:text-4xl text-forest mb-4">
              Karnataka Seasonal Wheel
            </h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              A four-season calendar tuned to the Western Ghats. Discover what to plant, observe, 
              and harvest each season.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <KarnatakaSeasonalWheel />
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-padding bg-cream" data-testid="projects-grid">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <p className="text-sm tracking-[0.2em] uppercase text-earth mb-4">Case Studies</p>
            <h2 className="font-serif text-3xl md:text-4xl text-forest">
              Featured Projects
            </h2>
          </motion.div>

          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap justify-center gap-3 mb-12"
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  filter === category
                    ? 'bg-forest text-white'
                    : 'bg-white text-foreground/70 hover:bg-forest/10'
                }`}
              >
                {category}
              </button>
            ))}
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-4 border-forest border-t-transparent rounded-full spinner" />
            </div>
          ) : (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="grid md:grid-cols-2 gap-8"
            >
              {filteredProjects.map((project) => (
                <motion.div
                  key={project.id}
                  variants={fadeInUp}
                  layout
                >
                  <TiltCard intensity={8}>
                    <Link 
                      to={`/projects/${project.id}`}
                      className="group block relative overflow-hidden rounded-2xl h-[400px] lg:h-[450px] shadow-lg"
                      data-testid={`project-${project.id}`}
                    >
                      <motion.img 
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.7 }}
                      />
                      <div className="absolute inset-0 project-overlay" />
                      <div className="absolute bottom-0 left-0 right-0 p-8">
                        <motion.span 
                          className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs mb-4"
                          whileHover={{ scale: 1.05 }}
                        >
                          {project.category}
                        </motion.span>
                        <h3 className="font-serif text-2xl text-white mb-2">{project.title}</h3>
                        <div className="flex items-center text-white/70 text-sm mb-4">
                          <MapPin className="w-4 h-4 mr-1" />
                          {project.location}
                        </div>
                        <p className="text-white/80 text-sm line-clamp-2 mb-4">
                          {project.description}
                        </p>
                        <motion.div 
                          className="flex items-center text-white/80 group-hover:text-white transition-colors"
                          whileHover={{ x: 5 }}
                        >
                          <span className="text-sm font-medium">View Project</span>
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </motion.div>
                      </div>
                    </Link>
                  </TiltCard>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Contact Form Section */}
      <section ref={contactRef} className="section-padding bg-forest" data-testid="projects-contact">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-white"
            >
              <p className="text-sm tracking-[0.2em] uppercase text-white/60 mb-4">Get Started</p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                Start Your<br />
                <span className="italic">Ecological Project</span>
              </h2>
              <p className="text-white/80 leading-relaxed mb-8">
                Inspired by what you've seen? Every landscape transformation begins with a conversation. 
                Tell us about your land and we'll explore the possibilities together.
              </p>
              
              <div className="space-y-4 text-white/70">
                <p className="flex items-center gap-3">
                  <span className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center text-sm">1</span>
                  Share your land details
                </p>
                <p className="flex items-center gap-3">
                  <span className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center text-sm">2</span>
                  We'll review and respond within 48 hours
                </p>
                <p className="flex items-center gap-3">
                  <span className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center text-sm">3</span>
                  Schedule a site visit or consultation
                </p>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ delay: 0.2 }}
            >
              {formSuccess ? (
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center text-white">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Send className="w-8 h-8" />
                  </div>
                  <h3 className="font-serif text-2xl mb-4">Message Sent!</h3>
                  <p className="text-white/80">
                    We'll review your inquiry and get back to you within 48 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-sm rounded-2xl p-8" data-testid="projects-contact-form">
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white/80 text-sm">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your name"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-11"
                        data-testid="projects-input-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white/80 text-sm">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-11"
                        data-testid="projects-input-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="space-y-2">
                      <Label htmlFor="location" className="text-white/80 text-sm">Land Location</Label>
                      <Input
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="District, State"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-11"
                        data-testid="projects-input-location"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="land_size" className="text-white/80 text-sm">Land Size</Label>
                      <Input
                        id="land_size"
                        name="land_size"
                        value={formData.land_size}
                        onChange={handleChange}
                        placeholder="e.g., 5 acres"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-11"
                        data-testid="projects-input-land-size"
                      />
                    </div>
                  </div>

                  <div className="space-y-2 mb-6">
                    <Label htmlFor="message" className="text-white/80 text-sm">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      placeholder="Tell us about your land and what you'd like to achieve..."
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl resize-none"
                      data-testid="projects-input-message"
                    />
                  </div>

                  {formError && (
                    <div className="p-3 bg-clay/20 border border-clay/30 rounded-xl text-white text-sm mb-4">
                      {formError}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={formLoading}
                    className="w-full bg-white text-forest hover:bg-white/90 rounded-full py-5 font-semibold text-sm tracking-wider uppercase"
                    data-testid="projects-submit-btn"
                  >
                    {formLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-forest border-t-transparent rounded-full spinner mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Inquiry
                      </>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
